import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Calendar, User, TrainFront, MapPin, TrendingUp, Building2, ArrowRight, ChevronLeft, ChevronRight, Download, ShieldCheck, Zap, Factory, Trees, Plane, Globe2, Briefcase, Users } from 'lucide-react';
import { CorridorReserveModal } from './CorridorReserveModal';

// Simple global event bus (avoid prop drilling into feature cards)
const openReserveModal = (corridor) => {
  window.dispatchEvent(new CustomEvent('acremitra:open-corridor-reserve', { detail: { corridor } }));
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public URL of the KMC NOC Impact Brief PDF (uploaded by team)
const PINK_LINE_PDF_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_plot-marketplace-8/artifacts/o8b556ua_AcreMitra_Pink_Line_KMC_NOC_Impact_Brief_Official_Letterhead.pdf';

// Public URL of the Kalyani Greenfield Airport Impact Report PDF
const KALYANI_AIRPORT_PDF_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_plot-marketplace-8/artifacts/8p58zti8_AcreMitra_Kalyani_Greenfield_Airport_Impact_Report-1.pdf';

// Sora-2 generated Kalyani Airport video
const KALYANI_AIRPORT_VIDEO_URL = '/videos/kalyani-airport.mp4';

// Pink Line video (generated via Sora-2, saved to public/videos)
const PINK_LINE_VIDEO_URL = '/videos/pink-line-metro.mp4';

// ---------- Motion-graphic SVG: metro expansion animation (SVG fallback if video is missing) ----------
const MetroMotionGraphicFallback = () => (
  <div className="relative w-full aspect-[16/9] rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 overflow-hidden border border-emerald-800/40 shadow-2xl" data-testid="pink-metro-motion">
    {/* Grid backdrop */}
    <div className="absolute inset-0 opacity-20"
      style={{
        backgroundImage: 'linear-gradient(rgba(16,185,129,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.35) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }}
    />

    {/* Rising skyline (5 buildings that grow) */}
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full">
      <defs>
        <linearGradient id="pinkGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#f472b6" />
        </linearGradient>
        <linearGradient id="bldgGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0f766e" />
          <stop offset="100%" stopColor="#134e4a" />
        </linearGradient>
        <radialGradient id="stationGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#ec4899" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Buildings (animate height via CSS keyframes) */}
      {[
        { x: 60,  w: 55, delay: 0.0 },
        { x: 140, w: 65, delay: 0.4 },
        { x: 230, w: 50, delay: 0.8 },
        { x: 320, w: 70, delay: 1.2 },
        { x: 420, w: 55, delay: 1.6 },
        { x: 510, w: 75, delay: 2.0 },
        { x: 620, w: 55, delay: 2.4 },
        { x: 710, w: 60, delay: 2.8 },
      ].map((b, i) => (
        <rect
          key={i}
          x={b.x}
          y={330}
          width={b.w}
          height={0}
          fill="url(#bldgGrad)"
          className="am-bldg-rise"
          style={{ animationDelay: `${b.delay}s` }}
        >
          <animate attributeName="height" from="0" to={90 + (i % 3) * 40} dur="1.4s" begin={`${b.delay}s`} fill="freeze" />
          <animate attributeName="y" from="420" to={330 - (i % 3) * 40} dur="1.4s" begin={`${b.delay}s`} fill="freeze" />
        </rect>
      ))}

      {/* Metro track (dashed pink line) */}
      <line x1="20" y1="240" x2="780" y2="240" stroke="url(#pinkGrad)" strokeWidth="4" strokeDasharray="10 8" />

      {/* Stations */}
      {[
        { x: 100,  label: 'BT Road' },
        { x: 320,  label: 'New Barrackpore' },
        { x: 540,  label: 'Sodepur' },
        { x: 720,  label: 'Kalyani Expy' },
      ].map((s, i) => (
        <g key={i}>
          <circle cx={s.x} cy={240} r={22} fill="url(#stationGlow)">
            <animate attributeName="r" values="18;30;18" dur="2.4s" repeatCount="indefinite" begin={`${i * 0.5}s`} />
          </circle>
          <circle cx={s.x} cy={240} r={9} fill="#ec4899" stroke="#fff" strokeWidth="2" />
          <text x={s.x} y={215} fill="#fbcfe8" fontSize="12" textAnchor="middle" fontWeight="700" style={{ fontFamily: 'system-ui' }}>{s.label}</text>
        </g>
      ))}

      {/* Metro train — animated left→right loop */}
      <g className="am-metro-train">
        <rect x="-80" y="222" width="70" height="36" rx="8" fill="url(#pinkGrad)" stroke="#fce7f3" strokeWidth="2" />
        <rect x="-70" y="230" width="12" height="14" fill="#fce7f3" opacity="0.9" />
        <rect x="-52" y="230" width="12" height="14" fill="#fce7f3" opacity="0.9" />
        <rect x="-34" y="230" width="12" height="14" fill="#fce7f3" opacity="0.9" />
        <circle cx="-64" cy="264" r="4" fill="#1f2937" />
        <circle cx="-24" cy="264" r="4" fill="#1f2937" />
        <animateTransform attributeName="transform" type="translate" from="0 0" to="880 0" dur="6s" repeatCount="indefinite" />
      </g>

      {/* Coverage pulse rings (from Kalyani hub) */}
      <circle cx={720} cy={240} r={30} fill="none" stroke="#ec4899" strokeWidth="2" opacity="0.6">
        <animate attributeName="r" from="30" to="140" dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.6" to="0" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx={720} cy={240} r={30} fill="none" stroke="#ec4899" strokeWidth="2" opacity="0.6">
        <animate attributeName="r" from="30" to="140" dur="3s" repeatCount="indefinite" begin="1.5s" />
        <animate attributeName="opacity" from="0.6" to="0" dur="3s" repeatCount="indefinite" begin="1.5s" />
      </circle>
    </svg>

    {/* Overlay badges */}
    <div className="absolute top-4 left-4 flex items-center gap-2">
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-pink-300 bg-pink-900/40 border border-pink-400/40 px-2 py-1 rounded-full backdrop-blur">
        <TrainFront size={11} /> Pink Line · KMC Corridor
      </span>
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-300 bg-emerald-900/40 border border-emerald-400/40 px-2 py-1 rounded-full backdrop-blur">
        Live Impact Brief
      </span>
    </div>
  </div>
);

// Real video (Sora-2) with graceful fallback to the SVG motion graphic.
const MetroMotionGraphic = () => {
  const [videoOk, setVideoOk] = useState(true);
  return videoOk ? (
    <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-emerald-800/40 shadow-2xl bg-slate-900" data-testid="pink-metro-video">
      <video
        src={PINK_LINE_VIDEO_URL}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?w=1280&h=720&fit=crop"
        onError={() => setVideoOk(false)}
      />
      <div className="absolute top-4 left-4 flex items-center gap-2 pointer-events-none">
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-pink-100 bg-pink-900/60 border border-pink-400/40 px-2 py-1 rounded-full backdrop-blur">
          <TrainFront size={11} /> Pink Line · Corridor Preview
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-100 bg-emerald-900/60 border border-emerald-400/40 px-2 py-1 rounded-full backdrop-blur">
          AI Visualisation
        </span>
      </div>
    </div>
  ) : (
    <MetroMotionGraphicFallback />
  );
};

// ---------- Feature card: KMC Pink Metro Impact Brief ----------
const PinkMetroFeature = () => {
  const benefits = [
    { icon: TrendingUp, title: 'Plot valuation uplift', detail: 'Historically, Kolkata metro corridors added 18–32% to residential land value within 3 years of route notification (Rajarhat E-W benchmark).' },
    { icon: MapPin,     title: 'BT Road catchment',    detail: 'Belghoria–Sodepur–Panihati stretch becomes a direct 22-min ride to Esplanade — huge upside for plots within 1.5 km of proposed stations.' },
    { icon: Factory,    title: 'Kalyani corridor jobs', detail: 'Adjacency to Kalyani Expressway + AIIMS Kalyani drives white-collar demand; expect a 500-crore-plus MSME cluster along NH-12.' },
    { icon: Trees,      title: 'Cleaner suburbs',      detail: 'KMC NOC mandates green-buffer setbacks — an added lift for gated plot communities marketed by Acre Mitra.' },
  ];

  const stations = [
    { name: 'BT Road (Belghoria)', km: '0 km' },
    { name: 'Khardaha',            km: '4.2 km' },
    { name: 'New Barrackpore',     km: '9.1 km' },
    { name: 'Sodepur',             km: '13.5 km' },
    { name: 'Panihati',            km: '17.8 km' },
    { name: 'Kalyani Expy Jn.',    km: '24.6 km' },
  ];

  return (
    <div className="mb-16 rounded-3xl bg-gradient-to-br from-slate-50 via-white to-pink-50/50 border border-pink-100 p-6 sm:p-8 lg:p-10 relative overflow-hidden" data-testid="pink-metro-feature">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-pink-200/40 blur-3xl -z-0" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-emerald-200/40 blur-3xl -z-0" />

      <div className="relative grid lg:grid-cols-2 gap-8 items-center">
        {/* LEFT — motion graphic */}
        <MetroMotionGraphic />

        {/* RIGHT — copy */}
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-pink-700 bg-pink-100 border border-pink-200 px-3 py-1.5 rounded-full">
            <ShieldCheck size={12} /> Breaking · KMC NOC granted
          </span>
          <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 leading-tight">
            Pink Metro to unlock <span className="text-pink-600">BT Road</span> &amp; <span className="text-emerald-700">Kalyani Expressway</span>
          </h3>
          <p className="text-slate-600 text-sm mt-3 leading-relaxed">
            The Kolkata Municipal Corporation has cleared the No-Objection Certificate for the Pink Line extension corridor — a 24.6 km alignment linking Belghoria (BT Road) to the Kalyani Expressway junction. Acre Mitra&rsquo;s research team has published a full <strong>Impact Brief</strong> on the letterhead, covering land uplift, station catchments, and corridor build-out through 2030.
          </p>

          {/* CTA row */}
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={PINK_LINE_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm px-4 py-2.5 rounded-full transition-colors"
              data-testid="pink-metro-pdf-download"
            >
              <Download size={14} /> Download PDF Impact Brief
            </a>
            <a
              href="#pink-metro-project-overview"
              className="inline-flex items-center gap-1.5 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold text-sm px-4 py-2 rounded-full transition-colors"
              data-testid="pink-metro-explore"
            >
              Explore project overview <ArrowRight size={14} />
            </a>
          </div>

          {/* Quick stats */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { n: '24.6 km', l: 'Corridor Length' },
              { n: '6', l: 'New Stations' },
              { n: '2030', l: 'Target Completion' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 text-center">
                <div className="text-lg sm:text-xl font-black text-slate-900">{s.n}</div>
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PROJECT OVERVIEW — full-width section below */}
      <div id="pink-metro-project-overview" className="relative mt-10 pt-8 border-t border-slate-200/70">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
            <Building2 size={12} /> Project Overview
          </span>
          <h4 className="text-2xl sm:text-3xl font-black text-slate-900 mt-3">
            What the Pink Line means for your <span className="text-pink-600">plot value</span>
          </h4>
          <p className="text-slate-600 text-sm mt-2">
            Acre Mitra&rsquo;s research desk benchmarks new Pink Line stations against the East-West Metro corridor (opened 2020) to forecast realistic upside for BT Road &amp; Kalyani-adjacent plots.
          </p>
        </div>

        {/* Benefit grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-10">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={i}
                className="group relative rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                data-testid={`pink-metro-benefit-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 to-pink-700 text-white grid place-items-center shadow-md">
                  <Icon size={20} strokeWidth={2.2} />
                </div>
                <h5 className="text-sm font-black text-slate-900 mt-4">{b.title}</h5>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{b.detail}</p>
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-pink-500 via-amber-400 to-emerald-500 group-hover:w-full transition-all duration-500 rounded-b-2xl" />
              </div>
            );
          })}
        </div>

        {/* Station list */}
        <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-7 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #ec4899 0, transparent 40%), radial-gradient(circle at 80% 80%, #10b981 0, transparent 40%)' }}
          />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <TrainFront size={18} className="text-pink-400" />
              <h5 className="text-sm font-black uppercase tracking-widest text-pink-300">Proposed station list</h5>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {stations.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                  <span className="w-7 h-7 rounded-full bg-pink-600 text-white grid place-items-center text-[11px] font-black shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{s.name}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{s.km}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3 text-[11px] text-slate-300">
              <Zap size={12} className="text-amber-400" />
              <span>Interchanges planned at Belghoria (BT Road bus terminus) and Kalyani Expressway Junction with NH-12 &amp; NBSTC hub.</span>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href={PINK_LINE_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-black text-sm px-6 py-3 rounded-full shadow-lg transition-all"
            data-testid="pink-metro-pdf-download-2"
          >
            <Download size={15} /> Full KMC NOC Impact Brief
          </a>
          <div className="text-[10px] text-slate-500 mt-2 uppercase tracking-widest font-black">Public research · Acre Mitra Research Desk · Feb 2026</div>
        </div>

        {/* Reserve-a-plot CTA (Pink Line corridor) */}
        <div className="mt-6 rounded-2xl bg-gradient-to-r from-pink-600 via-pink-700 to-slate-900 text-white p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/25 grid place-items-center shrink-0">
              <TrainFront size={18} className="text-pink-200" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-pink-200">Early-mover advantage</div>
              <div className="text-sm sm:text-base font-bold">Reserve a plot on the Pink Line corridor before the notification is gazetted.</div>
            </div>
          </div>
          <a
            href="/invest?corridor=pink-line"
            onClick={(e) => { e.preventDefault(); openReserveModal('pink-line'); }}
            className="inline-flex items-center gap-2 bg-white text-pink-700 hover:bg-pink-50 font-black text-sm px-5 py-2.5 rounded-full shadow-lg transition-all"
            data-testid="pink-metro-reserve-cta"
          >
            Reserve a plot in this corridor <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
};

// ---------- Feature card: Kalyani Greenfield Airport Impact Report ----------
const KalyaniAirportVideo = () => {
  const [videoOk, setVideoOk] = useState(true);
  return videoOk ? (
    <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-sky-800/40 shadow-2xl bg-slate-900" data-testid="kalyani-airport-video">
      <video
        src={KALYANI_AIRPORT_VIDEO_URL}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1280&h=720&fit=crop"
        onError={() => setVideoOk(false)}
      />
      <div className="absolute top-4 left-4 flex items-center gap-2 pointer-events-none">
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-sky-100 bg-sky-900/60 border border-sky-400/40 px-2 py-1 rounded-full backdrop-blur">
          <Plane size={11} /> Kalyani Greenfield · Corridor Preview
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-100 bg-emerald-900/60 border border-emerald-400/40 px-2 py-1 rounded-full backdrop-blur">
          AI Visualisation
        </span>
      </div>
    </div>
  ) : (
    <div className="relative w-full aspect-[16/9] rounded-2xl bg-gradient-to-br from-sky-900 via-slate-900 to-sky-950 overflow-hidden border border-sky-800/40 shadow-2xl grid place-items-center">
      <div className="text-center">
        <Plane size={56} className="mx-auto text-sky-300 animate-pulse" />
        <div className="text-xs font-black uppercase tracking-widest text-sky-200 mt-3">Kalyani Airport · AI Preview</div>
      </div>
    </div>
  );
};

const KalyaniAirportFeature = () => {
  const benefits = [
    { icon: Plane,       title: 'Direct global connect',   detail: 'A greenfield international airport (~35 km from Kolkata) turns Kalyani + North 24 Parganas into a genuine air-cargo hub for Bengal, Bihar & the North-East.' },
    { icon: Factory,     title: 'Manufacturing cluster',   detail: 'Aero-adjacent SEZ zoning is expected to attract electronics, pharma, cold-chain and logistics units along the Kalyani Expressway spine.' },
    { icon: Briefcase,   title: 'White-collar jobs',       detail: 'AIIMS Kalyani + IIIT Kalyani + new corporate parks near the airport are projected to create 40,000+ direct jobs by 2030.' },
    { icon: TrendingUp,  title: 'Plot value uplift',       detail: 'Historically, airport-adjacent land in Devanahalli / Hyderabad saw 3\u20135x uplift within 6 years of terminal opening \u2014 similar upside likely for BT Road, Barrackpore & Kalyani plots.' },
  ];

  const zones = [
    { name: 'Kalyani Township Extension', km: '0 km',  role: 'Terminal + hotels' },
    { name: 'AIIMS Kalyani corridor',     km: '3 km',  role: 'Med-tech & healthcare' },
    { name: 'Expressway Industrial Zone', km: '6 km',  role: 'Warehousing + SEZ' },
    { name: 'Panihati / Sodepur',         km: '18 km', role: 'Residential feeder' },
    { name: 'BT Road industrial belt',    km: '24 km', role: 'MSME + logistics'   },
    { name: 'Central Kolkata (via Expy)', km: '35 km', role: 'CBD linkage'        },
  ];

  return (
    <div className="mb-16 rounded-3xl bg-gradient-to-br from-sky-50 via-white to-emerald-50/40 border border-sky-100 p-6 sm:p-8 lg:p-10 relative overflow-hidden" data-testid="kalyani-airport-feature">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-sky-200/40 blur-3xl -z-0" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-200/40 blur-3xl -z-0" />

      <div className="relative grid lg:grid-cols-2 gap-8 items-center">
        {/* LEFT — copy */}
        <div className="order-2 lg:order-1">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-sky-700 bg-sky-100 border border-sky-200 px-3 py-1.5 rounded-full">
            <Globe2 size={12} /> Coming Soon · Greenfield Airport
          </span>
          <h3 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 leading-tight">
            <span className="text-sky-700">Kalyani</span> to get its own <span className="text-emerald-700">International Airport</span>
          </h3>
          <p className="text-slate-600 text-sm mt-3 leading-relaxed">
            The West Bengal government has notified land for a <strong>greenfield international airport near Kalyani</strong>, alongside the existing Kalyani Expressway. Once operational, it will be the second commercial airport in the Kolkata metropolitan region and is projected to trigger a decade of industrial + residential growth from Barrackpore all the way up to Ranaghat. Acre Mitra&rsquo;s research desk has published a full <strong>Impact Report</strong> on the letterhead covering plot uplift, feeder-zone map, and job-creation projections.
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={KALYANI_AIRPORT_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm px-4 py-2.5 rounded-full transition-colors"
              data-testid="kalyani-airport-pdf-download"
            >
              <Download size={14} /> Download PDF Impact Report
            </a>
            <a
              href="#kalyani-airport-overview"
              className="inline-flex items-center gap-1.5 border-2 border-sky-600 text-sky-700 hover:bg-sky-50 font-bold text-sm px-4 py-2 rounded-full transition-colors"
              data-testid="kalyani-airport-explore"
            >
              Explore project overview <ArrowRight size={14} />
            </a>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { n: '35 km',   l: 'From Kolkata CBD' },
              { n: '40k+',    l: 'Projected Jobs' },
              { n: '2030',    l: 'Target Opening' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 text-center">
                <div className="text-lg sm:text-xl font-black text-slate-900">{s.n}</div>
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — video */}
        <div className="order-1 lg:order-2">
          <KalyaniAirportVideo />
        </div>
      </div>

      {/* PROJECT OVERVIEW */}
      <div id="kalyani-airport-overview" className="relative mt-10 pt-8 border-t border-slate-200/70">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-sky-700 bg-sky-50 border border-sky-200 px-3 py-1.5 rounded-full">
            <Building2 size={12} /> Project Overview
          </span>
          <h4 className="text-2xl sm:text-3xl font-black text-slate-900 mt-3">
            How the airport turns Kalyani into a <span className="text-sky-700">tier-1 industrial zone</span>
          </h4>
          <p className="text-slate-600 text-sm mt-2">
            Acre Mitra benchmarks the Kalyani greenfield project against Devanahalli (Bangalore) and Shamshabad (Hyderabad) to forecast realistic upside for corridor plots.
          </p>
        </div>

        {/* Benefit grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-10">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="group relative rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden" data-testid={`kalyani-airport-benefit-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 text-white grid place-items-center shadow-md">
                  <Icon size={20} strokeWidth={2.2} />
                </div>
                <h5 className="text-sm font-black text-slate-900 mt-4">{b.title}</h5>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{b.detail}</p>
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-sky-500 via-amber-400 to-emerald-500 group-hover:w-full transition-all duration-500 rounded-b-2xl" />
              </div>
            );
          })}
        </div>

        {/* Feeder zone list */}
        <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-7 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #0ea5e9 0, transparent 40%), radial-gradient(circle at 80% 80%, #10b981 0, transparent 40%)' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <Plane size={18} className="text-sky-400" />
              <h5 className="text-sm font-black uppercase tracking-widest text-sky-300">Feeder growth zones</h5>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {zones.map((z, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                  <span className="w-7 h-7 rounded-full bg-sky-600 text-white grid place-items-center text-[11px] font-black shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{z.name}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{z.km} · {z.role}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3 text-[11px] text-slate-300">
              <Users size={12} className="text-amber-400" />
              <span>Corridor synergy: Airport + AIIMS Kalyani + Kalyani Expressway + Pink Line Metro will collectively cover 12 lakh residents by 2030.</span>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href={KALYANI_AIRPORT_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-black text-sm px-6 py-3 rounded-full shadow-lg transition-all"
            data-testid="kalyani-airport-pdf-download-2"
          >
            <Download size={15} /> Full Kalyani Greenfield Airport Impact Report
          </a>
          <div className="text-[10px] text-slate-500 mt-2 uppercase tracking-widest font-black">Public research · Acre Mitra Research Desk · Feb 2026</div>
        </div>

        {/* Reserve-a-plot CTA (Kalyani corridor) */}
        <div className="mt-6 rounded-2xl bg-gradient-to-r from-sky-700 via-sky-800 to-slate-900 text-white p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/25 grid place-items-center shrink-0">
              <Plane size={18} className="text-sky-200" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-sky-200">Airport-corridor uplift</div>
              <div className="text-sm sm:text-base font-bold">Reserve a plot along the Kalyani Expressway before the airport tender opens.</div>
            </div>
          </div>
          <a
            href="/invest?corridor=kalyani-airport"
            onClick={(e) => { e.preventDefault(); openReserveModal('kalyani-airport'); }}
            className="inline-flex items-center gap-2 bg-white text-sky-700 hover:bg-sky-50 font-black text-sm px-5 py-2.5 rounded-full shadow-lg transition-all"
            data-testid="kalyani-airport-reserve-cta"
          >
            Reserve a plot in this corridor <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
};


// ---------- Auto-rotating mobile carousel wrapper for the two featured news items ----------
const NewsFeaturesCarousel = ({ features }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const total = features.length;
  const goTo = (n) => setIdx(((n % total) + total) % total);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % total), 9000);
    return () => clearInterval(t);
  }, [paused, total]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setPaused(true);
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    if (Math.abs(dx) > 40) { if (dx < 0) goTo(idx + 1); else goTo(idx - 1); }
    touchStartX.current = null;
    touchDeltaX.current = 0;
    setTimeout(() => setPaused(false), 4000);
  };

  return (
    <>
      {/* Desktop / tablet: stacked */}
      <div className="hidden md:block">
        {features.map((f) => (
          <React.Fragment key={f.key}>{f.node}</React.Fragment>
        ))}
      </div>

      {/* Mobile: auto-rotating carousel */}
      <div className="md:hidden" data-testid="news-features-carousel">
        <div
          className="relative overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
            {features.map((f) => (
              <div key={f.key} className="w-full shrink-0 px-0.5">{f.node}</div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 px-2">
          <button type="button" onClick={() => { goTo(idx - 1); setPaused(true); setTimeout(() => setPaused(false), 4000); }} className="w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center text-slate-700 hover:bg-emerald-50 active:scale-95 transition" aria-label="Previous news" data-testid="news-carousel-prev">
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-1.5" role="tablist" data-testid="news-carousel-dots">
            {features.map((_, i) => (
              <button key={i} type="button" aria-label={`Go to news ${i + 1}`} onClick={() => { goTo(i); setPaused(true); setTimeout(() => setPaused(false), 4000); }} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-6 bg-emerald-600' : 'w-1.5 bg-slate-300'}`} data-testid={`news-carousel-dot-${i}`} />
            ))}
          </div>
          <button type="button" onClick={() => { goTo(idx + 1); setPaused(true); setTimeout(() => setPaused(false), 4000); }} className="w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center text-slate-700 hover:bg-emerald-50 active:scale-95 transition" aria-label="Next news" data-testid="news-carousel-next">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="mt-2 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">
          Swipe to browse · {idx + 1} of {total}
        </div>
      </div>
    </>
  );
};


export const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reserveCorridor, setReserveCorridor] = useState(null);

  useEffect(() => {
    fetchNews();
    const onOpen = (e) => setReserveCorridor(e?.detail?.corridor || null);
    window.addEventListener('acremitra:open-corridor-reserve', onOpen);
    return () => window.removeEventListener('acremitra:open-corridor-reserve', onOpen);
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="news" className="am-reveal py-20 bg-white">
      {reserveCorridor && <CorridorReserveModal corridor={reserveCorridor} onClose={() => setReserveCorridor(null)} />}
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Latest News</h2>
          <p className="text-lg text-slate-600">Stay updated with Acre Mitra</p>
        </div>

        {/* Featured news carousel — desktop: stacked; mobile: auto-rotating */}
        <NewsFeaturesCarousel features={[
          { key: 'pink-metro',      node: <PinkMetroFeature /> },
          { key: 'kalyani-airport', node: <KalyaniAirportFeature /> },
        ]} />

        {loading ? (
          <div className="text-center">Loading...</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {news.map((item) => (
              <div key={item.id} className="bg-slate-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img
                  src={item.image.match(/^\/api\/(uploads|files)\//) ? `${process.env.REACT_APP_BACKEND_URL}${item.image}` : item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 mb-4 line-clamp-3">{item.content}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <User size={16} />
                      {item.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      {new Date(item.published_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
